import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, User, Search, Menu, X, ChevronDown } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { getProducts } from '../lib/api';

const navItems = [
  { label: 'Home', to: '/' },
  { label: 'Lab Tested COAs', to: '/coas' },
  { label: 'News & Updates', to: '/news' },
  { label: 'About Us', to: '/about' },
  { label: 'Track Your Order', to: '/track' },
  { label: 'Contact', to: '/contact' },
];

const shopNav = [
  { label: 'Shop All', to: '/shop' },
  { label: 'Featured Blends', to: '/shop?category=Featured+Blends' },
  { label: 'Supplements', to: '/shop?category=Supplements' },
  { label: 'Research Topicals', to: '/shop?category=Research+Topicals' },
];

const Header = () => {
  const { totalQty } = useCart();
  const [menuOpen, setMenuOpen] = useState(false);
  const [byNameOpen, setByNameOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [products, setProducts] = useState([]);
  const navigate = useNavigate();

  React.useEffect(() => {
    getProducts({ sort: 'name-asc' })
      .then(setProducts)
      .catch(() => {});
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (query.trim()) {
      navigate(`/shop?search=${encodeURIComponent(query.trim())}`);
      setSearchOpen(false);
      setQuery('');
    }
  };

  return (
    <header className="sticky top-0 z-50 bg-black border-b border-zinc-900">
      <div className="max-w-[1400px] mx-auto px-4 lg:px-8">
        {/* Top row */}
        <div className="flex items-center justify-between py-4">
          {/* Mobile menu */}
          <button
            className="lg:hidden text-white"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Menu"
          >
            {menuOpen ? <X size={26} /> : <Menu size={26} />}
          </button>

          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            <div className="flex flex-col leading-none">
              <svg width="140" height="20" viewBox="0 0 140 20" className="text-[#909090]">
                <polyline
                  points="0,15 15,15 22,5 30,18 38,2 46,15 60,15 68,8 76,15 140,15"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                />
              </svg>
              <span className="font-bold tracking-[0.18em] text-white text-xl md:text-2xl">
                HYDRO <span className="text-[#909090]">HEALTH</span>
              </span>
            </div>
          </Link>

          {/* Desktop primary nav */}
          <nav className="hidden lg:flex items-center gap-7 text-sm text-white">
            {navItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className="hover:text-[#909090] transition-colors duration-200"
              >
                {item.label}
              </Link>
            ))}
          </nav>

          {/* Right icons */}
          <div className="flex items-center gap-4">
            <button
              onClick={() => setSearchOpen(!searchOpen)}
              className="text-white hover:text-[#909090] transition-colors"
              aria-label="Search"
            >
              <Search size={20} />
            </button>
            <Link
              to="/account"
              className="text-white hover:text-[#909090] transition-colors hidden sm:block"
              aria-label="Account"
            >
              <User size={22} />
            </Link>
            <Link
              to="/cart"
              className="relative text-white hover:text-[#909090] transition-colors"
              aria-label="Cart"
            >
              <ShoppingCart size={22} />
              <span className="absolute -top-2 -right-2 bg-[#909090] text-black text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">
                {totalQty}
              </span>
            </Link>
          </div>
        </div>

        {/* Secondary shop nav */}
        <div className="hidden lg:flex items-center justify-center gap-8 pb-4 text-sm text-white">
          {shopNav.map((s) => (
            <Link
              key={s.to}
              to={s.to}
              className="hover:text-[#909090] transition-colors"
            >
              {s.label}
            </Link>
          ))}
          <div
            className="relative pb-2"
            onMouseEnter={() => setByNameOpen(true)}
            onMouseLeave={() => setByNameOpen(false)}
          >
            <button className="flex items-center gap-1 hover:text-[#909090] transition-colors">
              By Name <ChevronDown size={14} />
            </button>
            {byNameOpen && (
              <div className="absolute top-full left-1/2 -translate-x-1/2 w-72 max-h-96 overflow-y-auto bg-zinc-950 border border-zinc-800 rounded-md shadow-xl py-2 z-50">
                {products.map((p) => (
                    <Link
                      key={p.id}
                      to={`/product/${p.slug}`}
                      onClick={() => setByNameOpen(false)}
                      className="block px-4 py-2 text-sm text-zinc-300 hover:bg-zinc-900 hover:text-[#909090]"
                    >
                      {p.name}
                    </Link>
                  ))}
              </div>
            )}
          </div>
        </div>

        {/* Search overlay */}
        {searchOpen && (
          <form onSubmit={handleSearch} className="pb-4 flex gap-2">
            <input
              autoFocus
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search peptides..."
              className="flex-1 bg-zinc-900 border border-zinc-700 text-white placeholder-zinc-500 px-4 py-2 rounded focus:outline-none focus:border-[#909090]"
            />
            <button
              type="submit"
              className="bg-[#909090] text-black px-5 py-2 rounded font-medium hover:bg-[#a8a8a8] transition-colors"
            >
              Search
            </button>
          </form>
        )}

        {/* Mobile menu */}
        {menuOpen && (
          <div className="lg:hidden pb-4 space-y-1 border-t border-zinc-900 pt-3">
            {[...navItems, ...shopNav].map((item) => (
              <Link
                key={item.to}
                to={item.to}
                onClick={() => setMenuOpen(false)}
                className="block py-2 text-zinc-200 hover:text-[#909090]"
              >
                {item.label}
              </Link>
            ))}
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
